import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, provide, Component } from 'vue';
import { useStore } from "@/composables/useStore";
import { isSupportedRailItem, RailResponse, RailResponseType } from '@/types/cms';
import { RailBottomFocusId, RailTopFocusId } from '@/composables/useRailNavigation';
import { railFocusMemory } from '@/helpers/railFocusMemory';

import HorizontalRail from '@/components/rails/HorizontalRail.vue';
import CarouselRail from '@/components/rails/CarouselRail.vue';
import ForYouRail from '@/components/rails/ForYouRail.vue';
import ContinueWatchingRail from '@/components/rails/ContinueWatchingRail.vue';
import SplitPanelRail from '@/components/rails/SplitPanelRail.vue';
import LiveRail from '@/components/rails/LiveRail.vue';
import SingleContentRail from '@/components/rails/SingleContentRail.vue';
import MarketingRail from '@/components/rails/MarketingRail.vue';
import LuckyDipRail from '@/components/rails/LuckyDipRail.vue';
import StaffReviewRail from '@/components/rails/StaffReviewRail.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'RailsContainer',
  props: {
    rails: null,
    topFocusId: null,
    bottomFocusId: null,
    rememberFocus: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  rails: RailResponse[]
  topFocusId?: string
  bottomFocusId?: string
  rememberFocus?: boolean
};



// Read by `useRailNavigation` in each rail, so the ten rail components need no
// prop of their own. A getter, so a rail reads the current id when focus leaves
// it rather than whichever one happened to be set when it mounted.
provide(RailTopFocusId, () => props.topFocusId);
provide(RailBottomFocusId, () => props.bottomFocusId);

const railComponents: Record<RailResponseType, Component> = {
  [RailResponseType.Horizontal]: HorizontalRail,
  [RailResponseType.Carousel]: CarouselRail,
  [RailResponseType.ForYou]: ForYouRail,
  [RailResponseType.ContinueWatching]: ContinueWatchingRail,
  [RailResponseType.CategoryRail]: SplitPanelRail,
  [RailResponseType.Live]: LiveRail,
  [RailResponseType.SingleContent]: SingleContentRail,
  [RailResponseType.DoubleContent]: SplitPanelRail,
  [RailResponseType.Marketing]: MarketingRail,
  [RailResponseType.LuckyDip]: LuckyDipRail,
  [RailResponseType.StaffReview]: StaffReviewRail,
};


function showRail(rail: RailResponse): boolean {
  if (!(rail.type in railComponents)) {
    console.warn(`[rails] Unknown rail type "${rail.type}" (rail ${rail.id}) — skipping`);
    return false;
  }

  //Only show when logged in
  const loggedInOnlyRails: RailResponseType[] = [
    RailResponseType.ForYou,
    RailResponseType.ContinueWatching
  ];

  //Ensure we have at least one renderable item
  const horizontalRailTypes: RailResponseType[] = [
    RailResponseType.Horizontal,
    RailResponseType.Live];

  if (loggedInOnlyRails.includes(rail.type) && !isLoggedIn.value) {
    return false;
  }

  //Dynamic so these are ok
  if (loggedInOnlyRails.includes(rail.type)) {
    return true;
  }

  //Ensure Lucky Dip has items before showing
  if (rail.type === RailResponseType.LuckyDip) {
    return (rail.luckyDipItems?.length ?? 0) > 0;
  }

  //Before items check
  if (rail.type === RailResponseType.Marketing) {
    return rail.image !== null;
  }
  
  if (rail.type === RailResponseType.StaffReview) {
    return rail.staffReview != null && rail.image !== null;
  }

  //As we need items for focus events app breaks without items so ditch rails that don't have them
  if (!rail.items || rail.items.length === 0) {
    return false;
  }

  if (horizontalRailTypes.includes(rail.type)) {
    return rail.items.some(isSupportedRailItem);
  }

  //The carousel renders SERIES only so check for at least one
  if (rail.type === RailResponseType.Carousel) {
    return rail.items.some((item) => item.type === 'SERIES');
  }

  if (rail.type === RailResponseType.CategoryRail) {
    return rail.items.some((item) => item.type === 'CATEGORY');
  }

  return true;
}


const { useGetter } = useStore();

const isLoggedIn = useGetter<boolean>('isLoggedIn');

const railsToShow = ref(3);


const railsOnDisplay = computed(() =>
  props.rails
    .filter((rail) => showRail(rail))
    .slice(0, railsToShow.value)
);

const railDidNavigateDown = (railIndex: number) => {
  if (railIndex - 2 < railsToShow.value) {
    railsToShow.value += 1;
  }
};

const recordFocusedRail = (event: FocusEvent) => {
  if (!props.rememberFocus) return;

  const focusedId = (event.target as HTMLElement | null)?.id;
  const railIndex = focusedId?.match(/^rail-(\d+)-/)?.[1];

  if (railIndex !== undefined) railFocusMemory.set(Number(railIndex));
};




return { __sfc: true,props, railComponents, showRail, useGetter, isLoggedIn, railsToShow, railsOnDisplay, railDidNavigateDown, recordFocusedRail }
}

})